import { Link, useLocation } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

interface Suggestion {
  path: string
  title: string
  reason: string
}

const nextMap: Record<string, Suggestion[]> = {
  '/ai-salary-gap': [
    { path: '/skills-roi', title: 'AI Skills ROI', reason: 'Close the gap by learning the highest-ROI skill.' },
    { path: '/lifetime-earnings', title: 'Lifetime Earnings', reason: 'See the 30-year value of that gap.' },
    { path: '/displacement-risk', title: 'Displacement Risk', reason: 'Understand the downside of not upskilling.' }
  ],
  '/skills-roi': [
    { path: '/ai-salary-gap', title: 'AI Salary Gap', reason: 'See the salary gap the skill will close.' },
    { path: '/ai-job-search', title: 'Job Search Accelerator', reason: 'Turn the skill into more callbacks.' },
    { path: '/ai-career-transition', title: 'Career Transition', reason: 'Compare upskilling with switching roles.' }
  ],
  '/ai-productivity': [
    { path: '/hire-vs-ai', title: 'Hire vs AI', reason: 'Scale the same logic to a team decision.' },
    { path: '/ai-raise-calculator', title: 'Raise Calculator', reason: 'Turn saved hours into a raise request.' },
    { path: '/automation-savings', title: 'Automation Savings', reason: 'Estimate the dollar value of automation.' }
  ],
  '/hire-vs-ai': [
    { path: '/ai-vs-offshore', title: 'AI vs Offshore', reason: 'Compare AI with another alternative.' },
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Calculate the value of each hour saved.' },
    { path: '/ai-team-sizing', title: 'Team Size', reason: 'Plan headcount with AI augmentation.' }
  ],
  '/automation-savings': [
    { path: '/ai-error-cost', title: 'AI Error Cost', reason: 'Add the cost of errors to the savings model.' },
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Estimate the value of hours saved.' },
    { path: '/hire-vs-ai', title: 'Hire vs AI', reason: 'Compare automation to new headcount.' }
  ],
  '/displacement-risk': [
    { path: '/ai-salary-gap', title: 'AI Salary Gap', reason: 'See the upside of closing your gap.' },
    { path: '/skills-roi', title: 'Skills ROI', reason: 'Find the fastest skill to lower your risk.' },
    { path: '/ai-career-transition', title: 'Career Transition', reason: 'Compare staying versus switching.' }
  ],
  '/lifetime-earnings': [
    { path: '/ai-salary-gap', title: 'AI Salary Gap', reason: 'Calculate the annual gap feeding your NPV.' },
    { path: '/skills-roi', title: 'Skills ROI', reason: 'See the payback period of skills that boost earnings.' },
    { path: '/ai-career-transition', title: 'Career Transition', reason: 'Model a career switch.' }
  ],
  '/ai-tax-deductions': [
    { path: '/ai-tool-budget', title: 'Personal AI Budget', reason: 'Plan how much to spend on tools.' },
    { path: '/ai-subscription-roi', title: 'Subscription ROI', reason: 'Check if each subscription is worth it.' }
  ],
  '/ai-freelance-rate': [
    { path: '/ai-consulting-rate', title: 'Consulting Rate', reason: 'Set day and weekly rates.' },
    { path: '/ai-side-hustle', title: 'Side Hustle', reason: 'Estimate profit and break-even.' },
    { path: '/ai-tax-deductions', title: 'Tax Deductions', reason: 'Deduct AI tools from freelance income.' }
  ],
  '/ai-subscription-roi': [
    { path: '/ai-tool-budget', title: 'Personal AI Budget', reason: 'Decide how many subscriptions to keep.' },
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Convert saved time into dollars.' }
  ],
  '/ai-raise-calculator': [
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Calculate the value you create.' },
    { path: '/ai-salary-gap', title: 'AI Salary Gap', reason: 'Benchmark your market premium.' }
  ],
  '/ai-side-hustle': [
    { path: '/ai-freelance-rate', title: 'Freelance Rate', reason: 'Set your hourly and project rates.' },
    { path: '/ai-subscription-roi', title: 'Subscription ROI', reason: 'Keep only profitable tools.' },
    { path: '/ai-tax-deductions', title: 'Tax Deductions', reason: 'Write off your side hustle tools.' }
  ],
  '/ai-api-cost': [
    { path: '/local-vs-cloud-ai', title: 'Local vs Cloud AI', reason: 'Compare hosting costs for the same API.' },
    { path: '/multi-agent-workflow', title: 'Multi-Agent Workflow', reason: 'Estimate hidden token costs.' }
  ],
  '/ai-vs-offshore': [
    { path: '/hire-vs-ai', title: 'Hire vs AI', reason: 'Compare AI with a local hire.' },
    { path: '/automation-savings', title: 'Automation Savings', reason: 'Estimate the value of automation.' }
  ],
  '/ai-training-roi': [
    { path: '/ai-team-sizing', title: 'Team Size', reason: 'Plan headcount after upskilling.' },
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Calculate per-person value.' }
  ],
  '/ai-content-roi': [
    { path: '/ai-ad-creative-roi', title: 'Ad Creative ROI', reason: 'Compare creative formats.' },
    { path: '/ai-job-search', title: 'Job Search', reason: 'Use AI content for your own career.' }
  ],
  '/ai-consulting-rate': [
    { path: '/ai-freelance-rate', title: 'Freelance Rate', reason: 'Set hourly rates for solo work.' },
    { path: '/ai-revenue-impact', title: 'Revenue Impact', reason: 'Show value to consulting clients.' }
  ],
  '/ai-revenue-impact': [
    { path: '/ai-pricing-elasticity', title: 'Pricing Elasticity', reason: 'Find the optimal price.' },
    { path: '/lead-response-roi', title: 'Lead Response ROI', reason: 'Capture more revenue from leads.' }
  ],
  '/ai-job-search': [
    { path: '/ai-salary-gap', title: 'AI Salary Gap', reason: 'Negotiate with your market premium.' },
    { path: '/skills-roi', title: 'Skills ROI', reason: 'Pick the skill that gets callbacks.' }
  ],
  '/ai-pricing-elasticity': [
    { path: '/ai-revenue-impact', title: 'Revenue Impact', reason: 'Model the full business impact.' }
  ],
  '/ai-compliance-cost': [
    { path: '/ai-error-cost', title: 'AI Error Cost', reason: 'Add error risk to compliance planning.' },
    { path: '/ai-cyber-risk', title: 'Cyber Risk', reason: 'Estimate AI-driven security risk.' }
  ],
  '/ai-team-sizing': [
    { path: '/ai-training-roi', title: 'Training ROI', reason: 'Upskill the team before hiring.' },
    { path: '/hire-vs-ai', title: 'Hire vs AI', reason: 'Compare headcount and automation.' }
  ],
  '/ai-error-cost': [
    { path: '/ai-compliance-cost', title: 'Compliance Cost', reason: 'Budget for governance and audit.' },
    { path: '/automation-savings', title: 'Automation Savings', reason: 'Net the savings against error cost.' }
  ],
  '/ai-support-roi': [
    { path: '/lead-response-roi', title: 'Lead Response ROI', reason: 'Speed up customer response.' },
    { path: '/ai-error-cost', title: 'AI Error Cost', reason: 'Estimate chatbot error risk.' }
  ],
  '/ai-tool-budget': [
    { path: '/ai-subscription-roi', title: 'Subscription ROI', reason: 'Check each tool individually.' },
    { path: '/ai-tax-deductions', title: 'Tax Deductions', reason: 'Deduct eligible tool costs.' }
  ],
  '/ai-healthcare-admin': [
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Convert saved hours into dollars.' }
  ],
  '/ai-portfolio-exposure': [
    { path: '/ai-cyber-risk', title: 'Cyber Risk', reason: 'Assess portfolio risk from AI attacks.' }
  ],
  '/ai-legal-cost': [
    { path: '/ai-compliance-cost', title: 'Compliance Cost', reason: 'Add governance costs to legal planning.' }
  ],
  '/ai-recruiting-roi': [
    { path: '/ai-team-sizing', title: 'Team Size', reason: 'Plan headcount after recruitment savings.' }
  ],
  '/ai-ad-creative-roi': [
    { path: '/ai-content-roi', title: 'Content ROI', reason: 'Compare AI content formats.' }
  ],
  '/ai-carbon-cost': [
    { path: '/ai-api-cost', title: 'API Cost', reason: 'Estimate cost and emissions together.' }
  ],
  '/ai-coding-productivity': [
    { path: '/ai-api-cost', title: 'API Cost', reason: 'Estimate the cost of AI code generation.' },
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Calculate the value of hours saved.' }
  ],
  '/local-vs-cloud-ai': [
    { path: '/ai-api-cost', title: 'API Cost', reason: 'Compare cloud API pricing.' },
    { path: '/saas-vs-custom-agent', title: 'SaaS vs Custom Agent', reason: 'Compare build and buy options.' }
  ],
  '/rag-vs-fine-tuning': [
    { path: '/ai-api-cost', title: 'API Cost', reason: 'Estimate API costs of each approach.' }
  ],
  '/data-readiness-cost': [
    { path: '/multi-agent-workflow', title: 'Multi-Agent Workflow', reason: 'Estimate post-cleaning AI costs.' }
  ],
  '/multi-agent-workflow': [
    { path: '/ai-api-cost', title: 'API Cost', reason: 'Compare per-token API pricing.' },
    { path: '/data-readiness-cost', title: 'Data Readiness', reason: 'Clean data before multi-agent setup.' }
  ],
  '/ai-voice-video': [
    { path: '/ai-api-cost', title: 'API Cost', reason: 'Estimate API usage for media generation.' }
  ],
  '/saas-vs-custom-agent': [
    { path: '/local-vs-cloud-ai', title: 'Local vs Cloud AI', reason: 'Compare hosting models.' },
    { path: '/hire-vs-ai', title: 'Hire vs AI', reason: 'Compare build cost to hiring.' }
  ],
  '/lead-response-roi': [
    { path: '/ai-support-roi', title: 'Support ROI', reason: 'Improve customer response speed.' },
    { path: '/ai-revenue-impact', title: 'Revenue Impact', reason: 'Model top-line growth.' }
  ],
  '/data-degradation-saver': [
    { path: '/data-readiness-cost', title: 'Data Readiness', reason: 'Prevent degradation at the source.' }
  ],
  '/ai-cyber-risk': [
    { path: '/ai-error-cost', title: 'AI Error Cost', reason: 'Add error risk to security planning.' }
  ],
  '/ai-career-transition': [
    { path: '/ai-salary-gap', title: 'AI Salary Gap', reason: 'See the salary gap in your target role.' },
    { path: '/skills-roi', title: 'Skills ROI', reason: 'Find the fastest skill to bridge the gap.' }
  ],
  '/rto-vs-ai-nomad': [
    { path: '/ai-productivity', title: 'Productivity Value', reason: 'Calculate remote AI productivity gains.' }
  ]
}

export default function NextCalculators() {
  const location = useLocation()
  const path = location.pathname.replace(/\/$/, '') || '/'
  const suggestions = nextMap[path]
  if (!suggestions || suggestions.length === 0) return null

  return (
    <section className="bg-slate-50 border-t border-slate-200 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-5">Next calculator to try</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {suggestions.map(s => (
            <Link
              key={s.path}
              to={s.path}
              className="card p-5 hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-slate-900">{s.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary-600 transition-colors" />
              </div>
              <p className="text-sm text-slate-600">{s.reason}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
